"use client";

import { useMemo, useState } from "react";
import { marked } from "marked";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";

// Styles du rendu Markdown (panneau clair facon "document"), scopés au conteneur.
const PREVIEW_CLASS =
    "text-[14px] text-gray-700 leading-relaxed break-words " +
    "[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-2 [&_h1]:mt-0 " +
    "[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-4 [&_h2]:mb-2 " +
    "[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-3 [&_h3]:mb-1 " +
    "[&_p]:mb-3 " +
    "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ul]:space-y-1 " +
    "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_ol]:space-y-1 " +
    "[&_li]:marker:text-sky-500 " +
    "[&_a]:text-sky-600 [&_a]:underline [&_a]:font-medium " +
    "[&_strong]:font-bold [&_strong]:text-gray-900 " +
    "[&_em]:italic " +
    "[&_code]:bg-gray-100 [&_code]:text-rose-600 [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px] [&_code]:font-mono " +
    "[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:overflow-x-auto [&_pre]:text-[13px] [&_pre]:mb-3 " +
    "[&_pre_code]:bg-transparent [&_pre_code]:text-gray-100 [&_pre_code]:p-0 " +
    "[&_blockquote]:border-l-4 [&_blockquote]:border-sky-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-500 [&_blockquote]:italic [&_blockquote]:my-3 " +
    "[&_hr]:my-4 [&_hr]:border-gray-200";

export default function ReactMarkdownPreview() {
    const t = useTranslations();
    const [text, setText] = useState(() => t("React_markdown_sample"));

    const html = useMemo(
        () => marked.parse(text || "", { breaks: true, gfm: true }),
        [text],
    );

    return (
        <section id="react-markdown" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("React_markdown_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("React_markdown_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:react"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        MarkdownPreview.jsx
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("React_markdown_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative grid lg:grid-cols-2">
                    {/* ÉDITEUR */}
                    <div className="flex flex-col lg:border-r border-white/10">
                        <div className="flex items-center gap-2 px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.16em] text-gray-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                            {t("React_markdown_editor_label")}
                        </div>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            spellCheck={false}
                            aria-label={t("React_markdown_editor_label")}
                            className="w-full flex-1 min-h-[320px] resize-none bg-transparent px-5 pb-5 font-mono text-[13px] text-gray-200 leading-relaxed outline-hidden placeholder:text-gray-600"
                            placeholder={t("React_markdown_placeholder")}
                        />
                    </div>

                    {/* APERÇU */}
                    <div className="flex flex-col bg-black/20">
                        <div className="flex items-center gap-2 px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.16em] text-gray-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {t("React_markdown_preview_label")}
                        </div>
                        <div className="px-5 pb-5 flex-1 min-h-[320px]">
                            <div className="h-full rounded-2xl bg-white p-5 overflow-y-auto max-h-[440px] shadow-inner">
                                <div
                                    className={PREVIEW_CLASS}
                                    dangerouslySetInnerHTML={{ __html: html }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("React_markdown_caption")}
            </p>
        </section>
    );
}
